const SAFE_SESSION_TOKEN = /^[A-Za-z0-9_-]{8,191}$/;
const SAFE_SLUG = /^[a-z0-9][a-z0-9_-]{0,190}$/;
const FAVOURITE_RESOURCES = new Set(['characters', 'roleplays']);
const MEMORY_FIELDS = new Set(['memory_value', 'category', 'importance']);

function validMemoryPayload(memory, partial = false) {
    if (!memory || typeof memory !== 'object' || Array.isArray(memory)) return false;
    const keys = Object.keys(memory);
    if (keys.length < 1 || keys.some((key) => !MEMORY_FIELDS.has(key))
        || (!partial && !Object.hasOwn(memory, 'memory_value'))) return false;
    if (Object.hasOwn(memory, 'memory_value')) {
        if (typeof memory.memory_value !== 'string') return false;
        const value = memory.memory_value.trim();
        if (!value || Array.from(value).length > 1000) return false;
    }
    if (Object.hasOwn(memory, 'category')
        && (typeof memory.category !== 'string' || !/^[a-z][a-z0-9_-]{0,63}$/.test(memory.category))) return false;
    return !Object.hasOwn(memory, 'importance')
        || (Number.isInteger(memory.importance) && memory.importance >= 0 && memory.importance <= 100);
}

function normalizeConfig(config, locationOrigin) {
    if (!config || config.enabled !== true || !config.baseUrl || !config.wordpressNonce) {
        return null;
    }

    let baseUrl;
    try {
        baseUrl = new URL(String(config.baseUrl), locationOrigin);
    } catch (error) {
        return null;
    }

    if (baseUrl.origin !== locationOrigin || !baseUrl.pathname.endsWith('/api/v2/')) {
        return null;
    }

    return {
        baseUrl: baseUrl.toString(),
        wordpressNonce: String(config.wordpressNonce),
    };
}

function apiError(response, payload) {
    const error = new Error(payload?.error?.message || `TalkieMate API request failed (${response.status}).`);
    error.name = 'TalkieMateApiError';
    error.status = response.status;
    error.code = payload?.error?.code || 'api_request_failed';
    return error;
}

function assertEnvelope(response, payload) {
    if (!payload || typeof payload !== 'object' || !('data' in payload) || !('meta' in payload) || !('error' in payload)) {
        const error = new Error('TalkieMate API returned an invalid response envelope.');
        error.name = 'TalkieMateApiError';
        error.status = response.status;
        error.code = 'invalid_response';
        throw error;
    }

    if (!response.ok || payload.error) {
        throw apiError(response, payload);
    }

    return payload;
}

function createTalkieMateApiV2Client({
    config,
    fetchImpl = window.fetch.bind(window),
    locationOrigin = window.location.origin,
    now = () => Date.now(),
} = {}) {
    const normalized = normalizeConfig(config, locationOrigin);
    if (!normalized || typeof fetchImpl !== 'function') {
        return null;
    }

    let accessToken = '';
    let accessTokenExpiresAt = 0;
    let exchangePromise = null;
    let bootstrapPromise = null;

    const parseResponse = async (response) => {
        let payload;
        try {
            payload = await response.json();
        } catch (error) {
            payload = null;
        }
        return assertEnvelope(response, payload);
    };

    const exchange = async (force = false) => {
        if (!force && accessToken && accessTokenExpiresAt - now() > 30000) {
            return accessToken;
        }
        if (exchangePromise) {
            return exchangePromise;
        }

        exchangePromise = (async () => {
            const response = await fetchImpl(new URL('auth/exchange', normalized.baseUrl).toString(), {
                method: 'POST',
                credentials: 'same-origin',
                cache: 'no-store',
                headers: {
                    Accept: 'application/json',
                    'X-WP-Nonce': normalized.wordpressNonce,
                },
            });
            const payload = await parseResponse(response);
            const token = payload?.data?.access_token;
            const expiresIn = Number(payload?.data?.expires_in);

            if (payload?.data?.token_type !== 'Bearer'
                || typeof token !== 'string'
                || token.length < 32
                || !Number.isFinite(expiresIn)
                || expiresIn <= 0) {
                const error = new Error('TalkieMate API returned an invalid access token.');
                error.name = 'TalkieMateApiError';
                error.code = 'invalid_token_response';
                throw error;
            }

            accessToken = token;
            accessTokenExpiresAt = now() + (expiresIn * 1000);
            return token;
        })();

        try {
            return await exchangePromise;
        } finally {
            exchangePromise = null;
        }
    };

    const request = async (path, options = {}, retry = true) => {
        const token = await exchange();
        const headers = new Headers(options.headers || {});
        headers.set('Accept', 'application/json');
        headers.set('Authorization', `Bearer ${token}`);

        const response = await fetchImpl(new URL(path.replace(/^\/+/, ''), normalized.baseUrl).toString(), {
            ...options,
            credentials: 'same-origin',
            cache: 'no-store',
            headers,
        });

        if (response.status === 401 && retry) {
            accessToken = '';
            accessTokenExpiresAt = 0;
            await exchange(true);
            return request(path, options, false);
        }

        return parseResponse(response);
    };

    return Object.freeze({
        bootstrap() {
            if (!bootstrapPromise) {
                bootstrapPromise = request('me/bootstrap')
                    .then((payload) => payload.data)
                    .catch((error) => {
                        bootstrapPromise = null;
                        throw error;
                    });
            }
            return bootstrapPromise;
        },
        listChatSessions(cursor = '') {
            const query = cursor ? `?cursor=${encodeURIComponent(cursor)}` : '';
            return request(`chat/sessions${query}`);
        },
        listFavourites(cursor = '') {
            const query = cursor ? `?cursor=${encodeURIComponent(cursor)}` : '';
            return request(`me/favourites${query}`);
        },
        readPreferences() {
            return request('me/preferences').then((payload) => payload.data);
        },
        updatePreferences(preferences) {
            if (!preferences || typeof preferences !== 'object' || Array.isArray(preferences)) {
                return Promise.reject(new TypeError('Invalid preferences.'));
            }
            const keys = Object.keys(preferences);
            if (keys.length < 1
                || keys.some((key) => !['memory_enabled', 'default_language'].includes(key))
                || ('memory_enabled' in preferences && typeof preferences.memory_enabled !== 'boolean')
                || ('default_language' in preferences
                    && !/^[a-z][a-z0-9_-]{0,31}$/.test(String(preferences.default_language)))) {
                return Promise.reject(new TypeError('Invalid preferences.'));
            }
            return request('me/preferences', {
                method: 'PATCH',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify(preferences),
            }).then((payload) => payload.data);
        },
        readNotifications() {
            return request('me/notifications').then((payload) => payload.data);
        },
        readProfile() {
            return request('me/profile').then((payload) => payload.data);
        },
        updateProfile(profile) {
            const allowed = new Set([
                'email', 'display_name', 'first_name', 'last_name', 'ai_nickname',
                'appearance', 'user_persona', 'gender', 'date_of_birth',
            ]);
            if (!profile || typeof profile !== 'object' || Array.isArray(profile)) {
                return Promise.reject(new TypeError('Invalid profile.'));
            }
            const keys = Object.keys(profile);
            if (keys.length < 1 || keys.some((key) => !allowed.has(key) || typeof profile[key] !== 'string')) {
                return Promise.reject(new TypeError('Invalid profile.'));
            }
            return request('me/profile', {
                method: 'PATCH',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify(profile),
            }).then((payload) => payload.data);
        },
        uploadAvatar(file) {
            if (!(file instanceof Blob) || file.size < 1 || file.size > 5 * 1024 * 1024
                || !['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
                return Promise.reject(new TypeError('Invalid avatar.'));
            }
            const body = new FormData();
            body.append('avatar', file, file.name || 'avatar');
            return request('me/avatar', {method: 'POST', body}).then((payload) => payload.data);
        },
        deleteAvatar() {
            return request('me/avatar', {method: 'DELETE'}).then((payload) => payload.data);
        },
        listMemories(sessionToken = '') {
            if (sessionToken && !SAFE_SESSION_TOKEN.test(String(sessionToken))) {
                return Promise.reject(new TypeError('Invalid chat session token.'));
            }
            const query = sessionToken ? `?session_token=${encodeURIComponent(sessionToken)}` : '';
            return request(`me/memories${query}`).then((payload) => payload.data);
        },
        createMemory(memory, sessionToken = '') {
            if (!validMemoryPayload(memory, false)
                || (sessionToken && !SAFE_SESSION_TOKEN.test(String(sessionToken)))) {
                return Promise.reject(new TypeError('Invalid memory.'));
            }
            const query = sessionToken ? `?session_token=${encodeURIComponent(sessionToken)}` : '';
            return request(`me/memories${query}`, {
                method: 'POST', headers: {'Content-Type': 'application/json'}, body: JSON.stringify(memory),
            }).then((payload) => payload.data);
        },
        updateMemory(id, memory, sessionToken = '') {
            id = String(id);
            if (!/^[1-9][0-9]{0,19}$/.test(id) || !validMemoryPayload(memory, true)
                || (sessionToken && !SAFE_SESSION_TOKEN.test(String(sessionToken)))) {
                return Promise.reject(new TypeError('Invalid memory.'));
            }
            const query = sessionToken ? `?session_token=${encodeURIComponent(sessionToken)}` : '';
            return request(`me/memories/${id}${query}`, {
                method: 'PATCH', headers: {'Content-Type': 'application/json'}, body: JSON.stringify(memory),
            }).then((payload) => payload.data);
        },
        deleteMemory(id, sessionToken = '') {
            id = String(id);
            if (!/^[1-9][0-9]{0,19}$/.test(id) || (sessionToken && !SAFE_SESSION_TOKEN.test(String(sessionToken)))) {
                return Promise.reject(new TypeError('Invalid memory.'));
            }
            const query = sessionToken ? `?session_token=${encodeURIComponent(sessionToken)}` : '';
            return request(`me/memories/${id}${query}`, {method: 'DELETE'}).then((payload) => payload.data);
        },
        listBackups() {
            return request('me/backups').then((payload) => payload.data);
        },
        createBackup(idempotencyKey) {
            if (!/^[A-Za-z0-9._:-]{16,128}$/.test(String(idempotencyKey))) {
                return Promise.reject(new TypeError('Invalid idempotency key.'));
            }
            return request('me/backups', {method: 'POST', headers: {'Idempotency-Key': String(idempotencyKey)}}).then((payload) => payload.data);
        },
        previewBackup(id) {
            id = String(id);
            if (!/^[1-9][0-9]{0,19}$/.test(id)) return Promise.reject(new TypeError('Invalid backup id.'));
            return request(`me/backups/${id}`).then((payload) => payload.data);
        },
        restoreBackup(id, idempotencyKey) {
            id = String(id);
            if (!/^[1-9][0-9]{0,19}$/.test(id) || !/^[A-Za-z0-9._:-]{16,128}$/.test(String(idempotencyKey))) {
                return Promise.reject(new TypeError('Invalid backup restore request.'));
            }
            return request(`me/backups/${id}/restore`, {method: 'POST', headers: {'Idempotency-Key': String(idempotencyKey)}}).then((payload) => payload.data);
        },
        deleteBackup(id) {
            id = String(id);
            if (!/^[1-9][0-9]{0,19}$/.test(id)) return Promise.reject(new TypeError('Invalid backup id.'));
            return request(`me/backups/${id}`, {method: 'DELETE'}).then((payload) => payload.data);
        },
        updateNotifications(operation, ids) {
            operation = String(operation);
            if (!['read', 'dismiss'].includes(operation)
                || !Array.isArray(ids) || ids.length < 1 || ids.length > 100
                || ids.some((id) => !/^[a-f0-9]{32}$/.test(String(id)))) {
                return Promise.reject(new TypeError('Invalid notification mutation.'));
            }
            return request('me/notifications', {
                method: 'PATCH',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({operation, ids: [...new Set(ids.map(String))]}),
            }).then((payload) => payload.data);
        },
        setFavourite(resource, slug, favourite) {
            resource = String(resource);
            slug = String(slug);
            if (!FAVOURITE_RESOURCES.has(resource) || !SAFE_SLUG.test(slug) || typeof favourite !== 'boolean') {
                return Promise.reject(new TypeError('Invalid favourite resource.'));
            }
            return request(`me/favourites/${resource}/${encodeURIComponent(slug)}`, {
                method: favourite ? 'PUT' : 'DELETE',
            });
        },
        readChatSession(token, cursor = '') {
            if (!SAFE_SESSION_TOKEN.test(String(token))) {
                return Promise.reject(new TypeError('Invalid chat session token.'));
            }
            const query = cursor ? `?cursor=${encodeURIComponent(cursor)}` : '';
            return request(`chat/sessions/${encodeURIComponent(token)}${query}`);
        },
        clearAccountState() {
            accessToken = '';
            accessTokenExpiresAt = 0;
            bootstrapPromise = null;
        },
    });
}

function installTalkieMateApiV2Client() {
    if (typeof window === 'undefined') {
        return null;
    }
    if (window.talkiemateApiV2Client) {
        return window.talkiemateApiV2Client;
    }

    const client = createTalkieMateApiV2Client({
        config: window.talkiemateApiV2Config,
    });
    if (client) {
        window.talkiemateApiV2Client = client;
    }
    return client;
}

export { createTalkieMateApiV2Client, installTalkieMateApiV2Client };
